import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { StateContext } from '../../../StateProvider/index.js';

import eventIcon from '../../../../assets/Console/Landing/eventIcon.png';
import incidentIcon from '../../../../assets/Console/Landing/incidentIcon.png';
import petIcon from '../../../../assets/Console/Landing/petIcon.png';

import './Promotions.css';

function Promotions() {
  var ctx = useContext(StateContext) || {};
  var navigate = useNavigate();

  // Incoming (optional)
  var incomingPromos = ctx.promotions;      // array
  var incomingKpi = ctx.promoSummary;       // { redemptionsToday, revenueLiftToday, avgDiscount }

  // Demo fallback
  var demoPromos = [
    {
      id: 'PR-1001',
      name: 'AptPartner10',
      code: 'APT10',
      type: 'Percent',
      value: 10,
      stackable: false,
      memberOnly: false,
      services: ['Retail', 'Daycare'],
      categories: ['Essentials'],
      skus: [],
      minSpend: 25,
      start: '2025-08-10T00:00:00-07:00',
      end: '2025-09-30T23:59:59-07:00',
      limitTotal: 500,
      limitPerCustomer: 3,
      active: true,
      archived: false,
      redemptions: 42
    },
    {
      id: 'PR-1002',
      name: 'GroomGlow5',
      code: 'GLOW5',
      type: 'Amount',
      value: 5,
      stackable: true,
      memberOnly: false,
      services: ['Grooming'],
      categories: [],
      skus: [],
      minSpend: 0,
      start: '2025-08-01T00:00:00-07:00',
      end: '2025-08-31T23:59:59-07:00',
      limitTotal: 9999,
      limitPerCustomer: 5,
      active: true,
      archived: false,
      redemptions: 77
    },
    {
      id: 'PR-1003',
      name: 'VIP Welcome',
      code: 'VIP20',
      type: 'Percent',
      value: 20,
      stackable: false,
      memberOnly: true,
      services: ['Retail', 'Daycare', 'Boarding'],
      categories: [],
      skus: [],
      minSpend: 0,
      start: '2025-07-01T00:00:00-07:00',
      end: '2025-07-31T23:59:59-07:00',
      limitTotal: 200,
      limitPerCustomer: 1,
      active: false,
      archived: true,
      redemptions: 120
    }
  ];

  // Local storage key so reception doesn’t lose edits on refresh
  var LS_KEY = 'pawsible.promotions.state.v1';

  // State
  var initialPromos = Array.isArray(incomingPromos) && incomingPromos.length ? incomingPromos : demoPromos;
  var _useState1 = useState(loadFromStorage(initialPromos, 'promos')), promos = _useState1[0], setPromos = _useState1[1];

  var _useState2 = useState(loadFromStorage('All', 'statusFilter')), statusFilter = _useState2[0], setStatusFilter = _useState2[1];
  var _useState3 = useState(loadFromStorage('', 'search')), search = _useState3[0], setSearch = _useState3[1];

  var _useState4 = useState(loadFromStorage(null, 'editing')), editing = _useState4[0], setEditing = _useState4[1]; // promo or null
  var _useState5 = useState(loadFromStorage('', 'previewCartTotal')), previewCartTotal = _useState5[0], setPreviewCartTotal = _useState5[1];

  var _useState6 = useState(''), formError = _useState6[0], setFormError = _useState6[1];
  var searchRef = useRef(null);

  // Sync storage on change
  useEffect(function () { persist('promos', promos); }, [promos]);
  useEffect(function () { persist('statusFilter', statusFilter); }, [statusFilter]);
  useEffect(function () { persist('search', search); }, [search]);
  useEffect(function () { persist('editing', editing); }, [editing]);
  useEffect(function () { persist('previewCartTotal', previewCartTotal); }, [previewCartTotal]);

  // When context sends fresh promos
  useEffect(function () {
    if (Array.isArray(incomingPromos) && incomingPromos.length) {
      setPromos(incomingPromos);
    }
  }, [incomingPromos]);

  // Quick keyboard focus: Cmd/Ctrl+K to focus search
  useEffect(function () {
    function onKey(e) {
      var isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      if ((isMac && e.metaKey && e.key.toLowerCase() === 'k') || (!isMac && e.ctrlKey && e.key.toLowerCase() === 'k')) {
        e.preventDefault();
        try { if (searchRef.current) searchRef.current.focus(); } catch (err) {}
      }
    }
    window.addEventListener('keydown', onKey);
    return function () { window.removeEventListener('keydown', onKey); };
  }, []);

  // KPI compute
  var kpis = useMemo(function () {
    var lift = incomingKpi && typeof incomingKpi.revenueLiftToday === 'number' ? incomingKpi.revenueLiftToday : 265;
    var red = incomingKpi && typeof incomingKpi.redemptionsToday === 'number' ? incomingKpi.redemptionsToday : 19;
    var avg = incomingKpi && typeof incomingKpi.avgDiscount === 'number' ? incomingKpi.avgDiscount : 7.8;
    var activeCount = 0;
    var i = 0;
    while (i < promos.length) {
      if (computeStatus(promos[i]) === 'Active') activeCount = activeCount + 1;
      i = i + 1;
    }
    return { revenueLiftToday: lift, redemptionsToday: red, avgDiscount: avg, activeCount: activeCount };
  }, [promos, incomingKpi]);

  // Helpers
  function persist(key, value) {
    try {
      var raw = loadWhole();
      raw[key] = value;
      localStorage.setItem(LS_KEY, JSON.stringify(raw));
    } catch (e) {}
  }
  function loadWhole() {
    try {
      var t = localStorage.getItem(LS_KEY);
      if (!t) return {};
      return JSON.parse(t) || {};
    } catch (e) { return {}; }
  }
  function loadFromStorage(fallback, key) {
    try {
      var raw = loadWhole();
      return typeof raw[key] === 'undefined' ? fallback : raw[key];
    } catch (e) { return fallback; }
  }

  function fmtMoney(n) {
    if (typeof n !== 'number' || isNaN(n)) return '$0.00';
    try { return n.toLocaleString(undefined, { style: 'currency', currency: 'USD' }); }
    catch (e) { return '$' + Number(n).toFixed(2); }
  }

  function fmtDate(s) {
    try {
      var d = new Date(s);
      var f = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' });
      return f.format(d);
    } catch (e) { return s; }
  }

  function computeStatus(promo) {
    if (promo.archived) return 'Archived';
    var now = new Date();
    var start = new Date(promo.start);
    var end = new Date(promo.end);
    if (promo.active && now >= start && now <= end) return 'Active';
    if (now < start) return 'Scheduled';
    if (now > end) return 'Expired';
    return promo.active ? 'Active' : 'Paused';
  }

  // Validations for editor
  function validateEditing(p) {
    if (!p) return 'Nothing to save.';
    if (!p.name || !p.code) return 'Name and Code are required.';
    if (String(p.code).trim().length < 3) return 'Code must be at least 3 characters.';
    if (p.type !== 'Percent' && p.type !== 'Amount') return 'Type must be Percent or Amount.';
    if (Number(p.value) <= 0) return 'Discount must be greater than 0.';
    if (p.type === 'Percent' && Number(p.value) > 100) return 'Percent cannot exceed 100.';
    var start = new Date(p.start);
    var end = new Date(p.end);
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return 'Start and End must be valid dates.';
    if (end < start) return 'End must be after Start.';
    if (Number(p.limitTotal) < 0 || Number(p.limitPerCustomer) < 0) return 'Limits cannot be negative.';

    // Unique code check (ignore self)
    var i = 0;
    var used = false;
    var codeUp = String(p.code).toUpperCase();
    for (i = 0; i < promos.length; i++) {
      var x = promos[i];
      if (x.id !== p.id && String(x.code).toUpperCase() === codeUp && !x.archived) { used = true; break; }
    }
    if (used) return 'Code already in use. Pick another.';

    return '';
  }

  // Filtered list (no chains)
  function visiblePromos() {
    var out = [];
    var i = 0;
    while (i < promos.length) {
      var p = promos[i];
      var status = computeStatus(p);
      var keepStatus = statusFilter === 'All' || status === statusFilter;
      var keepSearch = true;
      if (search && search.trim().length > 0) {
        var s = search.toLowerCase();
        var blob = (p.id + ' ' + p.name + ' ' + p.code + ' ' + status).toLowerCase();
        keepSearch = blob.indexOf(s) >= 0;
      }
      if (keepStatus && keepSearch) out.push(p);
      i = i + 1;
    }
    // sort by start asc, then name
    var j = 0;
    while (j < out.length - 1) {
      var k = j + 1;
      while (k < out.length) {
        var aStart = new Date(out[j].start).getTime();
        var bStart = new Date(out[k].start).getTime();
        var swap = false;
        if (aStart > bStart) swap = true;
        else if (aStart === bStart && String(out[j].name).toLowerCase() > String(out[k].name).toLowerCase()) swap = true;
        if (swap) {
          var tmp = out[j]; out[j] = out[k]; out[k] = tmp;
        }
        k = k + 1;
      }
      j = j + 1;
    }
    return out;
  }

  function copyCode(code) {
    try {
      navigator.clipboard.writeText(code);
      toast('Code copied: ' + code);
    } catch (e) {
      toast('Copy failed. Code: ' + code);
    }
  }
  function toast(msg) {
    try {
      var el = document.createElement('div');
      el.className = 'promo-toast';
      el.textContent = msg;
      document.body.appendChild(el);
      setTimeout(function () { el.classList.add('show'); }, 10);
      setTimeout(function () { el.classList.remove('show'); }, 1800);
      setTimeout(function () { if (el && el.parentNode) el.parentNode.removeChild(el); }, 2200);
    } catch (e) { alert(msg); }
  }

  // Actions
  function setActive(id, flag) {
    setPromos(function (prev) {
      var next = [];
      var i = 0;
      while (i < prev.length) {
        var p = prev[i];
        if (p.id === id) next.push(Object.assign({}, p, { active: flag, archived: false }));
        else next.push(p);
        i = i + 1;
      }
      return next;
    });
  }

  function archive(id) {
    setPromos(function (prev) {
      var next = [];
      var i = 0;
      while (i < prev.length) {
        var p = prev[i];
        if (p.id === id) next.push(Object.assign({}, p, { archived: true, active: false }));
        else next.push(p);
        i = i + 1;
      }
      return next;
    });
  }

  function removePromo(id) {
    if (typeof window !== 'undefined' && !window.confirm('Delete this promotion?')) return;
    setPromos(function (prev) {
      var next = [];
      var i = 0;
      while (i < prev.length) {
        if (prev[i].id !== id) next.push(prev[i]);
        i = i + 1;
      }
      return next;
    });
  }

  function duplicatePromo(id) {
    setPromos(function (prev) {
      var clone = null;
      var i = 0;
      while (i < prev.length) {
        if (prev[i].id === id) {
          var base = prev[i];
          clone = Object.assign({}, base, {
            id: 'PR-' + Math.floor(Math.random() * 90000 + 10000),
            name: base.name + ' Copy',
            code: String(base.code).toUpperCase() + '2',
            active: false,
            archived: false,
            redemptions: 0
          });
        }
        i = i + 1;
      }
      if (!clone) return prev;
      var next = prev.slice();
      next.unshift(clone);
      return next;
    });
    toast('Duplicated. Edit before activating.');
  }

  function openCreate() {
    setFormError('');
    setEditing({
      id: 'PR-' + Math.floor(Math.random() * 90000 + 10000),
      name: '',
      code: '',
      type: 'Percent',
      value: 10,
      stackable: false,
      memberOnly: false,
      services: [],
      categories: [],
      skus: [],
      minSpend: 0,
      start: new Date().toISOString(),
      end: new Date(new Date().getTime() + 1000 * 60 * 60 * 24 * 30).toISOString(),
      limitTotal: 9999,
      limitPerCustomer: 1,
      active: true,
      archived: false,
      redemptions: 0
    });
    try { window.scrollTo({ top: 0 }); } catch (e) {}
  }

  function saveEditing(e) {
    if (e && e.preventDefault) e.preventDefault();
    if (!editing) return;
    var err = validateEditing(editing);
    if (err) { setFormError(err); return; }
    setFormError('');

    setPromos(function (prev) {
      var found = false;
      var next = [];
      var i = 0;
      while (i < prev.length) {
        if (prev[i].id === editing.id) {
          found = true;
          next.push(Object.assign({}, editing));
        } else {
          next.push(prev[i]);
        }
        i = i + 1;
      }
      if (!found) next.unshift(Object.assign({}, editing));
      return next;
    });
    setEditing(null);
    toast('Promotion saved.');
  }

  function cancelEditing(e) {
    if (e && e.preventDefault) e.preventDefault();
    setEditing(null);
    setFormError('');
  }

  function toggleArray(field, value) {
    setEditing(function (prev) {
      if (!prev) return prev;
      var arr = prev[field] || [];
      var exists = false;
      var i = 0;
      while (i < arr.length) {
        if (arr[i] === value) { exists = true; break; }
        i = i + 1;
      }
      var nextArr = [];
      if (exists) {
        var j = 0;
        while (j < arr.length) {
          if (arr[j] !== value) nextArr.push(arr[j]);
          j = j + 1;
        }
      } else {
        nextArr = arr.slice();
        nextArr.push(value);
      }
      var out = Object.assign({}, prev);
      out[field] = nextArr;
      return out;
    });
  }

  // Preview savings
  function calcPreviewSavings() {
    if (!editing) return { savings: 0, total: 0, meets: false };
    var t = Number(previewCartTotal) || 0;
    var min = Number(editing.minSpend) || 0;
    var meetsMin = t >= min;
    var savings = 0;
    if (meetsMin) {
      if (editing.type === 'Percent') savings = t * (Number(editing.value) / 100);
      else savings = Number(editing.value);
      if (savings > t) savings = t;
    }
    var total = t - savings;
    if (total < 0) total = 0;
    return { savings: savings, total: total, meets: meetsMin, min: min, t: t };
  }

  function sendToChannel(channel, promo) {
    var msg = 'Sent ' + promo.code + ' to ' + channel + ' audience.';
    toast(msg);
  }

  // Derived
  var list = visiblePromos();
  var preview = calcPreviewSavings();

  // Progress: total limit vs redemptions
  function usagePct(p) {
    var lim = Number(p.limitTotal) || 0;
    if (lim <= 0) return 0;
    var used = Number(p.redemptions) || 0;
    var pct = Math.round((used / lim) * 100);
    if (pct < 0) pct = 0;
    if (pct > 100) pct = 100;
    return pct;
  }

  return (
    <div className="promo-wrap">
      <h4 className="promo-header">Reception • Promotions</h4>
      <h4 className="promo-sub">Make the right offer at the right time for the right pet parent.</h4>

      {/* KPI Strip */}
      <section className="promo-brief">
        <div className="brief-item">Active <strong>{kpis.activeCount}</strong></div>
        <div className="brief-item">Redemptions Today <strong>{kpis.redemptionsToday}</strong></div>
        <div className="brief-item">Revenue Lift Today <strong>{fmtMoney(kpis.revenueLiftToday)}</strong></div>
        <div className="brief-item">Avg Discount <strong>{kpis.avgDiscount}%</strong></div>
      </section>

      {/* Editor */}
      <section className="promo-editor">
        <div className="promo-editor-head">
          <img src={petIcon} alt="icon" />
          <div className="promo-editor-title">{editing ? 'Edit Promotion' : 'Create Promotion'}</div>
          {!editing && (
            <button className="btn" onClick={openCreate} type="button">New Promotion</button>
          )}
        </div>

        {editing && (
          <form className="promo-form" onSubmit={saveEditing}>
            {formError ? <div className="promo-alert">{formError}</div> : null}
            <div className="form-grid">
              <div className="field">
                <label htmlFor="name">Name</label>
                <input
                  id="name"
                  value={editing.name}
                  onChange={function (e) { setEditing(Object.assign({}, editing, { name: e.target.value })); }}
                  placeholder="Apartment Partner 10"
                  required
                />
              </div>
              <div className="field">
                <label htmlFor="code">Code</label>
                <input
                  id="code"
                  value={editing.code}
                  onChange={function (e) { setEditing(Object.assign({}, editing, { code: e.target.value.toUpperCase() })); }}
                  placeholder="APT10"
                  required
                />
              </div>
              <div className="field">
                <label htmlFor="type">Type</label>
                <select
                  id="type"
                  value={editing.type}
                  onChange={function (e) { setEditing(Object.assign({}, editing, { type: e.target.value })); }}
                >
                  <option value="Percent">Percent</option>
                  <option value="Amount">Amount</option>
                </select>
              </div>
              <div className="field">
                <label htmlFor="value">{editing.type === 'Percent' ? 'Percent %' : 'Amount $'}</label>
                <input
                  id="value"
                  type="number"
                  min="0"
                  step="0.01"
                  value={editing.value}
                  onChange={function (e) { setEditing(Object.assign({}, editing, { value: Number(e.target.value) || 0 })); }}
                />
              </div>
              <div className="field">
                <label htmlFor="min">Min Spend</label>
                <input
                  id="min"
                  type="number"
                  min="0"
                  step="0.01"
                  value={editing.minSpend}
                  onChange={function (e) { setEditing(Object.assign({}, editing, { minSpend: Number(e.target.value) || 0 })); }}
                />
              </div>
              <div className="field">
                <label htmlFor="start">Start</label>
                <input
                  id="start"
                  type="datetime-local"
                  value={editing.start.slice(0, 16)}
                  onChange={function (e) { setEditing(Object.assign({}, editing, { start: new Date(e.target.value).toISOString() })); }}
                />
              </div>
              <div className="field">
                <label htmlFor="end">End</label>
                <input
                  id="end"
                  type="datetime-local"
                  value={editing.end.slice(0, 16)}
                  onChange={function (e) { setEditing(Object.assign({}, editing, { end: new Date(e.target.value).toISOString() })); }}
                />
              </div>
              <div className="field">
                <label htmlFor="limitTotal">Total Limit</label>
                <input
                  id="limitTotal"
                  type="number"
                  min="0"
                  value={editing.limitTotal}
                  onChange={function (e) { setEditing(Object.assign({}, editing, { limitTotal: Number(e.target.value) || 0 })); }}
                />
              </div>
              <div className="field">
                <label htmlFor="limitPer">Per Customer</label>
                <input
                  id="limitPer"
                  type="number"
                  min="0"
                  value={editing.limitPerCustomer}
                  onChange={function (e) { setEditing(Object.assign({}, editing, { limitPerCustomer: Number(e.target.value) || 0 })); }}
                />
              </div>
              <div className="field">
                <label htmlFor="member">Member Only</label>
                <select
                  id="member"
                  value={editing.memberOnly ? 'Yes' : 'No'}
                  onChange={function (e) { setEditing(Object.assign({}, editing, { memberOnly: e.target.value === 'Yes' })); }}
                >
                  <option value="No">No</option>
                  <option value="Yes">Yes</option>
                </select>
              </div>
              <div className="field">
                <label htmlFor="stack">Stackable</label>
                <select
                  id="stack"
                  value={editing.stackable ? 'Yes' : 'No'}
                  onChange={function (e) { setEditing(Object.assign({}, editing, { stackable: e.target.value === 'Yes' })); }}
                >
                  <option value="No">No</option>
                  <option value="Yes">Yes</option>
                </select>
              </div>
            </div>

            <div className="form-grid">
              <div className="field block">
                <label>Services</label>
                <div className="chips">
                  {['Retail', 'Daycare', 'Grooming', 'Boarding', 'Training'].map(function (s) {
                    var on = editing.services.indexOf(s) >= 0;
                    return (
                      <button
                        key={s}
                        className={'chip' + (on ? ' on' : '')}
                        type="button"
                        onClick={function () { toggleArray('services', s); }}
                      >{s}</button>
                    );
                  })}
                </div>
              </div>

              <div className="field block">
                <label>Categories</label>
                <div className="chips">
                  {['Food', 'Treats', 'Essentials', 'Accessories', 'Grooming', 'Health', 'Cat'].map(function (c) {
                    var on = editing.categories.indexOf(c) >= 0;
                    return (
                      <button
                        key={c}
                        className={'chip' + (on ? ' on' : '')}
                        type="button"
                        onClick={function () { toggleArray('categories', c); }}
                      >{c}</button>
                    );
                  })}
                </div>
              </div>

              <div className="field block">
                <label htmlFor="skus">SKUs (comma separated)</label>
                <input
                  id="skus"
                  placeholder="SKU-1001, SKU-1008"
                  value={(editing.skus || []).join(', ')}
                  onChange={function (e) {
                    var raw = e.target.value.split(',');
                    var list = [];
                    var i = 0;
                    while (i < raw.length) {
                      var v = raw[i].trim();
                      if (v) list.push(v);
                      i = i + 1;
                    }
                    setEditing(Object.assign({}, editing, { skus: list }));
                  }}
                />
              </div>
            </div>

            <div className="editor-actions">
              <div className="left">
                <button className="btn ghost" onClick={cancelEditing} type="button">Cancel</button>
                <button className="btn" type="submit">Save</button>
              </div>
              <div className="right preview">
                <img src={eventIcon} alt="preview" />
                <div className="preview-col">
                  <div className="preview-row">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="Cart total for preview"
                      value={previewCartTotal}
                      onChange={function (e) { setPreviewCartTotal(e.target.value); }}
                    />
                    <div className={'preview-pill' + (preview.meets ? '' : ' warn')}>
                      Savings {fmtMoney(preview.savings)}
                    </div>
                    <div className="preview-pill">After {fmtMoney(preview.total)}</div>
                  </div>
                  {!preview.meets && preview.t > 0 ? (
                    <div className="preview-note">Needs at least {fmtMoney(preview.min)} to qualify.</div>
                  ) : null}
                  <div className="send-row">
                    <button className="btn ghost" type="button" onClick={function(){ sendToChannel('SMS', editing); }}>Send SMS</button>
                    <button className="btn ghost" type="button" onClick={function(){ sendToChannel('Email', editing); }}>Send Email</button>
                    <button className="btn ghost" type="button" onClick={function(){ sendToChannel('Instagram', editing); }}>IG Caption</button>
                  </div>
                </div>
              </div>
            </div>
          </form>
        )}
      </section>

      {/* Filters */}
      <section className="promo-filters">
        <div className="filter-row">
          <div className="filter-item">
            <label htmlFor="status">Status</label>
            <select id="status" value={statusFilter} onChange={function (e) { setStatusFilter(e.target.value); }}>
              <option value="All">All</option>
              <option value="Active">Active</option>
              <option value="Scheduled">Scheduled</option>
              <option value="Expired">Expired</option>
              <option value="Paused">Paused</option>
              <option value="Archived">Archived</option>
            </select>
          </div>
          <div className="filter-item grow">
            <label htmlFor="search">Search</label>
            <input
              id="search"
              ref={searchRef}
              value={search}
              placeholder="Name or code"
              onChange={function (e) { setSearch(e.target.value); }}
            />
            <div className="hint">Press ⌘K or Ctrl K to jump here</div>
          </div>
          {!editing && (
            <div className="filter-item">
              <label>&nbsp;</label>
              <button className="btn" onClick={openCreate} type="button">New Promotion</button>
            </div>
          )}
        </div>
      </section>

      {/* List */}
      <section className="promo-list">
        {list.length === 0 && (
          <div className="empty-state">
            <img src={incidentIcon} alt="empty" />
            <p>No promotions match your filters.</p>
          </div>
        )}

        {list.map(function (p) {
          var status = computeStatus(p);
          var pct = usagePct(p);
          return (
            <article key={p.id} className="promo-card">
              <div className="card-main">
                <div className="row1">
                  <div className="name">{p.name}</div>
                  <div className={'badge ' + status.toLowerCase()}>{status}</div>
                </div>
                <div className="row2">
                  <button
                    className="code"
                    title="Copy code"
                    onClick={function(){ copyCode(p.code); }}
                    type="button"
                  >
                    {p.code}
                  </button>
                  <div className="dot">•</div>
                  <div className="type">{p.type === 'Percent' ? (p.value + '% off') : (fmtMoney(p.value) + ' off')}</div>
                  {p.minSpend > 0 ? (<><div className="dot">•</div><div className="min">Min {fmtMoney(p.minSpend)}</div></>) : null}
                </div>
                <div className="row3">
                  <div className="dates">{fmtDate(p.start)} to {fmtDate(p.end)}</div>
                  <div className="pill">{p.memberOnly ? 'Members' : 'All Customers'}</div>
                  {p.services && p.services.length > 0 ? <div className="pill">{p.services.join(', ')}</div> : null}
                  {p.categories && p.categories.length > 0 ? <div className="pill">{p.categories.join(', ')}</div> : null}
                </div>
                <div className="row4">
                  <div className="usage-bar">
                    <div className="usage-fill" style={{ width: (pct + '%') }} />
                  </div>
                  <div className="usage-text">{(p.redemptions || 0)} used {Number(p.limitTotal) > 0 ? ('/ ' + p.limitTotal) : ''}</div>
                </div>
              </div>

              <div className="card-side">
                <div className="actions">
                  <button className="btn ghost" type="button" onClick={function(){ setEditing(Object.assign({}, p)); window.scrollTo({ top: 0 }); }}>Edit</button>
                  {status === 'Active' ? (
                    <button className="btn" type="button" onClick={function(){ setActive(p.id, false); }}>Pause</button>
                  ) : null}
                  {(status === 'Paused' || status === 'Scheduled' || status === 'Expired') && !p.archived ? (
                    <button className="btn" type="button" onClick={function(){ setActive(p.id, true); }}>Activate</button>
                  ) : null}
                  {!p.archived ? <button className="btn ghost" type="button" onClick={function(){ duplicatePromo(p.id); }}>Duplicate</button> : null}
                  {!p.archived ? <button className="btn linkish" type="button" onClick={function(){ archive(p.id); }}>Archive</button> : null}
                  <button className="btn linkish" type="button" onClick={function(){ removePromo(p.id); }}>Delete</button>
                </div>
              </div>
            </article>
          );
        })}
      </section>
    </div>
  );
}

/* ===== utils (simple, no chains) ===== */
export default Promotions;
